import type { Building } from "building-types/building/buildingTypes";
import type { SectionBoundary, ComputedColumnPlacement } from "building-types/columns/columnBaseTypes";
import type {
  ColumnPlacementV1,
  ColumnV1,
  ColumnHorizontalPlacementDescriptor,
  PositionInput,
} from "building-types/columns/columnDeprecatedTypes";
import type { ColumnHorizontalAlignment, ColumnLateralAlignment } from "building-types/columns/columnTypesEnum";
import { ColumnPlacementType } from "building-types/columns/columnTypesEnum";
import { ColumnHorizontalDatum, WallLateralDatumAnchorAll } from "building-types/datums/datumsTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { Wall } from "building-types/walls/wallTypes";
import type { RWTable, Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { resolveAlignmentsToShifts } from "doc/utils/columnSharedUtils";
import { aabb2ByPolyLine2s } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { DIST_EPSILON } from "geom/core";
import { line2Eval, line2ProjectPt } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import { polyLine2Copy, polyLine2HasNaN, polyLine2Edge, polyLine2ByTwoPoints, polyLine2Reverse } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { rangeInclusiveByMinStep } from "geom/range";
import { vec2Dist, vec2DistSquared, vec2Dot, vec2IsInEdgeBounds, vec2Rotate } from "geom/vec2";
import { edgeHostedEntityGetLayerEdge } from "../../elements/edgePlacementUtils";
import { isCombinedGlobalInstanceMirror } from "../../generationTools/generateOpeningsTools";
import { getWallPath } from "../walls/wall";
import { solidWallLayerWithFallback } from "../walls/wallLayers";

/**
 * @deprecated
 *
 * @param columnPlacement the column placement to resolve
 * @param cachedhorizontalDatumTable the horizontal datum table to resolve the column placement on
 * @param sectionBoundaryBottom the bottom section boundary of the column
 * @param mirror whether to mirror the column placement
 * @param computedColumnPlacementArray the array of column placement information to add the column center positions to
 */
export function resolveColumnPlacementV1(
  walls: Building["walls"],
  room: Room,
  column: ColumnV1,
  cachedhorizontalDatumTable: RWTable<PolyLine2>,
  sectionBoundaryBottom: SectionBoundary,
  mirror: boolean,
  computedColumnPlacementArray: ComputedColumnPlacement[],
) {
  const effectiveMirror = isCombinedGlobalInstanceMirror(mirror, room);
  const {
    edgeId,
    placement: columnPlacement,
    horizontalDatum,
    horizontalAlignment,
    horizontalOffset,
    horizontalOffsetNormalized,
    lateralDatum,
    lateralAlignment,
    lateralOffset,
    lateralOffsetNormalized,
    isolatedAssemblyId,
  } = column;

  const { id: roomId } = room;
  const hashDatum = hashHorizontalDatum({ edgeId, datum: horizontalDatum, roomId });
  let baseline = polyLine2Copy(cachedhorizontalDatumTable[hashDatum]);
  //fallback to room edge if the horizontal datum is not available or 0 length or NaN
  if (
    !baseline ||
    vec2Dist(baseline[0], baseline[1], baseline[2], baseline[3]) < DIST_EPSILON ||
    polyLine2HasNaN(baseline)
  ) {
    const edgeIdx = room.edgeIds.indexOf(edgeId);
    baseline = polyLine2Edge(room.outline, edgeIdx);
  }

  const edgeLength = vec2Dist(baseline[0], baseline[1], baseline[2], baseline[3]);
  //this is the base section of the column which is used to determine the column's center position wrt the edge
  const { boundary } = sectionBoundaryBottom;
  const aabb = aabb2ByPolyLine2s([boundary]);
  if (!aabb) {
    return;
  }

  const wallArray = getWallArray(walls, room, edgeId);

  switch (columnPlacement.type) {
    case ColumnPlacementType.PARAM: {
      const { param, paramNormalized } = columnPlacement;
      const position = getObjectPlacementPositionV1(
        wallArray,
        baseline,
        { param: param, paramNormalized: paramNormalized },
        lateralDatum,
        aabb,
        { param: horizontalOffset, paramNormalized: horizontalOffsetNormalized },
        horizontalAlignment,
        { param: lateralOffset, paramNormalized: lateralOffsetNormalized },
        lateralAlignment,
        effectiveMirror,
      );
      if (!position) {
        return;
      }
      const [px, py] = position;
      computedColumnPlacementArray.push({
        position: [px, py],
        isolatedAssemblyId,
      });
      break;
    }
    case ColumnPlacementType.ARRAY: {
      const { spacing, shift, maxCount: limit } = columnPlacement;
      if (spacing > 0) {
        const spaceToFill = edgeLength - shift;
        const calcCount = Math.ceil(spaceToFill / spacing);
        const count = limit !== undefined ? Math.min(limit, calcCount) : calcCount;
        for (let j = 0; j < count; j++) {
          const param = shift + spacing * j;
          if (param >= 0 && param <= edgeLength) {
            const position = getObjectPlacementPositionV1(
              wallArray,
              baseline,
              { param: param, paramNormalized: false },
              lateralDatum,
              aabb,
              { param: horizontalOffset, paramNormalized: horizontalOffsetNormalized },
              horizontalAlignment,
              { param: lateralOffset, paramNormalized: lateralOffsetNormalized },
              lateralAlignment,
              effectiveMirror,
            );
            if (!position) {
              return;
            }
            const [px, py] = position;
            computedColumnPlacementArray.push({
              position: [px, py],
              isolatedAssemblyId,
            });
          }
        }
      }
      break;
    }
    case ColumnPlacementType.DIVISION: {
      const { segments } = columnPlacement;
      if (segments > 0) {
        const fact = 1 / segments;
        for (let j = 0; j <= segments; j++) {
          const position = getObjectPlacementPositionV1(
            wallArray,
            baseline,
            { param: fact * j, paramNormalized: true },
            lateralDatum,
            aabb,
            { param: horizontalOffset, paramNormalized: horizontalOffsetNormalized },
            horizontalAlignment,
            { param: lateralOffset, paramNormalized: lateralOffsetNormalized },
            lateralAlignment,
            effectiveMirror,
          );
          if (!position) {
            return;
          }
          const [px, py] = position;
          computedColumnPlacementArray.push({
            position: [px, py],
            isolatedAssemblyId,
          });
        }
      }
      break;
    }
    case ColumnPlacementType.MAX_DISTANCE: {
      const { maxDistance } = columnPlacement;
      const steps = rangeInclusiveByMinStep(0, edgeLength, maxDistance);
      for (const stepIndex of Object.keys(steps)) {
        const param = steps[stepIndex];
        if (param >= 0 && param <= edgeLength) {
          const position = getObjectPlacementPositionV1(
            wallArray,
            baseline,
            { param: param, paramNormalized: false },
            lateralDatum,
            aabb,
            { param: horizontalOffset, paramNormalized: horizontalOffsetNormalized },
            horizontalAlignment,
            { param: lateralOffset, paramNormalized: lateralOffsetNormalized },
            lateralAlignment,
            effectiveMirror,
          );
          if (!position) {
            return;
          }
          const [px, py] = position;
          computedColumnPlacementArray.push({
            position: [px, py],
            isolatedAssemblyId,
          });
        }
      }
      break;
    }
    default:
      assertUnreachable(columnPlacement);
  }
}

/**
 * @deprecated
 *
 * @param wallArray the wall array the object is in
 * @param horizontalDatumLine the horizontal datum line of the edge the object is on
 * @param horizontalParamPosition the horizontal parametric position of the object
 * @param lateralDatum the lateral datum of the object
 * @param aabbObject the aabb of the object
 * @param horizontalOffsetPosition the horizontal offset position of the object
 * @param horizontalAlignment the horizontal alignment of the object wrt to the final position
 * @param lateralOffsetPosition the lateral offset position of the object
 * @param lateralAlignment the lateral alignment of the object wrt to the final position
 * @param mirror whether to mirror the horizontal datum line
 * @returns the final position of the object
 */
export function getObjectPlacementPositionV1(
  wallArray: Wall[],
  horizontalDatumLine: PolyLine2,
  horizontalParamPosition: PositionInput,
  lateralDatum: WallLateralDatumAnchorAll,
  aabbObject: AABB2,
  horizontalOffsetPosition: PositionInput,
  horizontalAlignment: ColumnHorizontalAlignment,
  lateralOffsetPosition: PositionInput,
  lateralAlignment: ColumnLateralAlignment,
  mirror: boolean,
) {
  const horizontalDatumLineCopy = polyLine2Copy(horizontalDatumLine);
  if (mirror) {
    polyLine2Reverse(horizontalDatumLineCopy);
  }
  const edgeAngle = Math.atan2(
    horizontalDatumLineCopy[3] - horizontalDatumLineCopy[1],
    horizontalDatumLineCopy[2] - horizontalDatumLineCopy[0],
  );

  const { width, height, horizontalShift, lateralShift } = resolveAlignmentsToShifts(
    aabbObject,
    horizontalAlignment,
    lateralAlignment,
  );

  const { param: horizontalOffset, paramNormalized: horizontalOffsetNormalized } = horizontalOffsetPosition;
  let horizontalOffsetFinal: number = horizontalOffset;
  if (horizontalOffsetNormalized) {
    horizontalOffsetFinal *= width;
  }

  const { param: lateralOffset, paramNormalized: lateralOffsetNormalized } = lateralOffsetPosition;
  let lateralOffsetFinal: number = lateralOffset;
  if (lateralOffsetNormalized) {
    lateralOffsetFinal *= height;
  }

  const [offsetHorizontalFinal, offsetLateralFinal] = vec2Rotate(
    horizontalOffsetFinal + horizontalShift,
    (lateralOffsetFinal + lateralShift) * (mirror ? -1 : 1),
    edgeAngle,
  );

  const { param, paramNormalized } = horizontalParamPosition;
  const [x, y] = line2Eval(
    horizontalDatumLineCopy[0],
    horizontalDatumLineCopy[1],
    horizontalDatumLineCopy[2],
    horizontalDatumLineCopy[3],
    param,
    paramNormalized,
  );
  const dx = horizontalDatumLineCopy[2] - horizontalDatumLineCopy[0];
  const dy = horizontalDatumLineCopy[3] - horizontalDatumLineCopy[1];
  let datumEdge: PolyLine2 | undefined;
  let nearestWall: Wall | undefined;
  let distance: number = Number.POSITIVE_INFINITY;

  for (const wall of wallArray) {
    const { startX, startY, endX, endY } = getWallPath(wall);

    const edgeBound = vec2IsInEdgeBounds(x, y, startX, startY, endX, endY);

    if (!edgeBound) {
      const distSqr = vec2DistSquared(x, y, startX, startY);
      if (distSqr < distance || nearestWall === undefined) {
        distance = distSqr;
        nearestWall = wall;
      }
      continue;
    } else {
      nearestWall = wall;
      break;
    }
  }

  if (!nearestWall) {
    return null;
  }

  const { startX, startY, endX, endY } = getWallPath(nearestWall);

  let edgeVoid: PolyLine2 | undefined;
  let edgeVoidLeftOffset: number = 0;
  let edgeVoidRightOffset: number = 0;

  if (nearestWall?.wallType === WallType.VOID) {
    edgeVoid = polyLine2ByTwoPoints(startX, startY, endX, endY);
    const dxEdgeVoid: number = edgeVoid[2] - edgeVoid[0];
    const dyEdgeVoid: number = edgeVoid[3] - edgeVoid[1];
    //@deprecated concept of left and right offset
    edgeVoidLeftOffset = nearestWall.leftOffset;
    edgeVoidRightOffset = nearestWall.rightOffset;
    //orient the edgeVoid so that it is in the same direction as the edge
    if (vec2Dot(dx, dy, dxEdgeVoid, dyEdgeVoid) < 0) {
      polyLine2Reverse(edgeVoid);
      edgeVoidLeftOffset = nearestWall.rightOffset;
      edgeVoidRightOffset = nearestWall.leftOffset;
    }
  }

  switch (lateralDatum) {
    case WallLateralDatumAnchorAll.ROOM_EDGE: {
      datumEdge = polyLine2ByTwoPoints(startX, startY, endX, endY);
      break;
    }
    case WallLateralDatumAnchorAll.INSIDE_FACE_OF_FINISH: {
      if (nearestWall?.wallType === WallType.SOLID) {
        const { leftEdge } = solidWallLayerWithFallback(nearestWall, WallLayerType.FINISH);
        datumEdge = leftEdge;
      } else if (edgeVoid) {
        datumEdge = offsetPolyLine2(edgeVoid, -edgeVoidLeftOffset);
      }
      break;
    }
    case WallLateralDatumAnchorAll.INSIDE_FACE_OF_FRAMING: {
      if (nearestWall?.wallType === WallType.SOLID) {
        const { leftEdge } = solidWallLayerWithFallback(nearestWall, WallLayerType.STRUCTURE);
        datumEdge = leftEdge;
      } else if (edgeVoid) {
        datumEdge = offsetPolyLine2(edgeVoid, -edgeVoidLeftOffset);
      }
      break;
    }
    case WallLateralDatumAnchorAll.INSIDE_FACE_OF_SUBSTRATE: {
      if (nearestWall?.wallType === WallType.SOLID) {
        const { leftEdge } = solidWallLayerWithFallback(nearestWall, WallLayerType.SUBSTRATE);
        datumEdge = leftEdge;
      } else if (edgeVoid) {
        datumEdge = offsetPolyLine2(edgeVoid, -edgeVoidLeftOffset);
      }
      break;
    }
    case WallLateralDatumAnchorAll.OUTSIDE_FACE_OF_FINISH: {
      if (nearestWall?.wallType === WallType.SOLID) {
        const { rightEdge } = solidWallLayerWithFallback(nearestWall, WallLayerType.FINISH);
        datumEdge = rightEdge;
      } else if (edgeVoid) {
        datumEdge = offsetPolyLine2(edgeVoid, edgeVoidRightOffset);
      }
      break;
    }
    case WallLateralDatumAnchorAll.OUTSIDE_FACE_OF_FRAMING: {
      if (nearestWall?.wallType === WallType.SOLID) {
        const { rightEdge } = solidWallLayerWithFallback(nearestWall, WallLayerType.STRUCTURE);
        datumEdge = rightEdge;
      } else if (edgeVoid) {
        datumEdge = offsetPolyLine2(edgeVoid, edgeVoidRightOffset);
      }
      break;
    }
    case WallLateralDatumAnchorAll.OUTSIDE_FACE_OF_SUBSTRATE: {
      if (nearestWall?.wallType === WallType.SOLID) {
        const { rightEdge } = solidWallLayerWithFallback(nearestWall, WallLayerType.SUBSTRATE);
        datumEdge = rightEdge;
      } else if (edgeVoid) {
        datumEdge = offsetPolyLine2(edgeVoid, edgeVoidRightOffset);
      }
      break;
    }
    case WallLateralDatumAnchorAll.WALL_OFFSET: {
      // TODO: implement this datum
      break;
    }
    default:
      assertUnreachable(lateralDatum);
  }

  if (!datumEdge) {
    return null;
  }

  //orient with the baseline
  if (vec2Dot(dx, dy, datumEdge[2] - datumEdge[0], datumEdge[3] - datumEdge[1]) < 0) {
    polyLine2Reverse(datumEdge);
  }

  const [latProjX, latProjY] = line2ProjectPt(datumEdge[0], datumEdge[1], datumEdge[2], datumEdge[3], x, y);

  return [latProjX + offsetHorizontalFinal, latProjY + offsetLateralFinal];
}

function getWallArray(walls: Table<WallSystem>, room: Room, edgeId: string): Wall[] {
  const wallIndex = walls[room.level.id].wallIndex;
  const edgeIdx = room.edgeIds.indexOf(edgeId);
  const strip = wallIndex.wallStripFromRoomEdgeIndex(room, edgeIdx);
  return strip?.toWallArray() ?? [];
}

const HORIZONTAL_DATUM_HASH_DELIMETER = "#.#";

/**
 * @deprecated
 */
export interface HashingOptions {
  readonly edgeId: string;
  readonly roomId: string;
  readonly datum: ColumnHorizontalDatum;
}

/**
 * @deprecated
 */
export function hashHorizontalDatum(h: HashingOptions, delimeter = HORIZONTAL_DATUM_HASH_DELIMETER) {
  const { edgeId, roomId, datum } = h;
  return [edgeId, roomId, datum].join(delimeter);
}

/**
 * @deprecated
 */
export function unhashHorizontalDatum(hash: string, delimeter = HORIZONTAL_DATUM_HASH_DELIMETER) {
  const [edgeId, roomId, datum] = hash.split(delimeter);
  return { edgeId, roomId, datum };
}

/**
 * @deprecated
 *
 * @param buildingInput stores edge hosted building entity input
 * @param room the room the column is in
 * @param edgeId the edge id to resolve the column placement on
 * @param horizontalDatumTable the horizontal datum table
 */
export function resolveHorizontalDatumTable(
  building: Pick<Building, "roomIndex" | "wallMasses" | "mirror">,
  room: Room,
  edgeId: string,
  horizontalDatumTable: RWTable<Float64Array> = {},
) {
  for (const datum of Object.values(ColumnHorizontalDatum)) {
    const datumHash = hashHorizontalDatum({ edgeId, datum, roomId: room.id });

    if (horizontalDatumTable[datumHash] !== undefined) {
      continue;
    } //edge polyline already calculated for room, edge and datum

    const colDescriptor = columnHorizontalPlacementDescriptorNew(edgeId, datum, room.id);

    switch (datum) {
      case ColumnHorizontalDatum.ROOM_EDGE:
      case ColumnHorizontalDatum.STRUCTURE:
      case ColumnHorizontalDatum.SUBSTRATE:
      case ColumnHorizontalDatum.OUTSIDE_OF_SURFACE: {
        const layerEdge = edgeHostedEntityGetLayerEdge(building, room, colDescriptor);
        if (!layerEdge) {
          continue;
        }
        horizontalDatumTable[datumHash] = layerEdge;
        break;
      }
      default:
        assertUnreachable(datum);
    }
  }
  return horizontalDatumTable;
}

/**
 * @deprecated
 */
export function columnHorizontalPlacementDescriptorNew(
  edgeId: string,
  horizontalDatum: ColumnHorizontalDatum,
  parentId: string,
  param?: number,
  paramNormalized?: boolean,
): ColumnHorizontalPlacementDescriptor {
  return { edgeId, parentId, horizontalDatum, param: param ?? 0, paramNormalized: !!paramNormalized };
}

/**
 * @deprecated
 */
export function fromPlacementColumnHorizontalPlacementDescriptorNewV1(
  column: ColumnV1,
): ColumnHorizontalPlacementDescriptor {
  const { edgeId, parentId, horizontalDatum, placement } = column;
  if (placement.type === ColumnPlacementType.PARAM) {
    const { param, paramNormalized } = placement;
    return columnHorizontalPlacementDescriptorNew(edgeId, horizontalDatum, parentId, param, paramNormalized);
  } else {
    return columnHorizontalPlacementDescriptorNew(edgeId, horizontalDatum, parentId);
  }
}

/**
 * @deprecated
 */
export function defaultColumnPlacementV1(columnPlacementType: ColumnPlacementType): ColumnPlacementV1 {
  switch (columnPlacementType) {
    case ColumnPlacementType.PARAM:
      return {
        type: ColumnPlacementType.PARAM,
        param: 0,
        paramNormalized: true,
      };
    case ColumnPlacementType.ARRAY:
      return {
        type: ColumnPlacementType.ARRAY,
        spacing: 24,
        shift: 24,
        maxCount: undefined,
      };
    case ColumnPlacementType.DIVISION:
      return {
        type: ColumnPlacementType.DIVISION,
        segments: 2,
      };
    case ColumnPlacementType.MAX_DISTANCE: {
      return {
        type: ColumnPlacementType.MAX_DISTANCE,
        maxDistance: 24,
      };
    }
    default:
      assertUnreachable(columnPlacementType);
  }
}
